import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import type { MessageThread } from '@swiss-magic/shared-types';
import { AppHeader } from '@/components/AppHeader';
import { Button } from '@/components/ui/button';
import { MessageList } from '@/components/messaging/MessageList';
import { MessageComposer } from '@/components/messaging/MessageComposer';
import {
  getMessageThread,
  markThreadRead,
  sendPlayerMessage,
} from '@/lib/api/messages';
import { ApiError } from '@/lib/api/core';
import { usePolledData } from '@/lib/usePolledData';
import { showErrorToast } from '@/lib/toast';

function Composer({ onSend }: { onSend: (body: string) => Promise<void> }) {
  const { t } = useTranslation('player');
  return (
    <MessageComposer
      onSend={onSend}
      labels={{
        placeholder: t('messages.thread.placeholder'),
        send: t('messages.thread.send'),
        sending: t('messages.thread.sending'),
        sendError: t('messages.thread.send_error'),
      }}
    />
  );
}

function ThreadShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('player');
  const navigate = useNavigate();
  return (
    <div
      data-testid="message-thread-page"
      className="flex min-h-screen flex-col bg-background"
    >
      <AppHeader />
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col">
        <div className="flex items-center gap-2 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            data-testid="message-thread-back"
            onClick={() => navigate('/player/messages')}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('messages.thread.back')}
          </Button>
          <h2 className="truncate text-lg font-semibold">{title}</h2>
        </div>
        {children}
      </div>
    </div>
  );
}

function ComposeView({ tournamentId }: { tournamentId: string }) {
  const { t } = useTranslation('player');
  const navigate = useNavigate();
  return (
    <ThreadShell title={t('messages.thread.new_title')}>
      <MessageList messages={[]} myRole="player" />
      <Composer
        onSend={async (body) => {
          const msg = await sendPlayerMessage(tournamentId, body);
          navigate(`/player/messages/${msg.threadId}`, { replace: true });
        }}
      />
    </ThreadShell>
  );
}

function ThreadView({ threadId }: { threadId: string }) {
  const { t } = useTranslation('player');
  const { data, message, refetch } = usePolledData<MessageThread>(
    () =>
      getMessageThread(threadId).catch((err: unknown) => {
        throw err instanceof ApiError ? new Error(err.userMessage) : err;
      }),
    [threadId],
  );
  useEffect(() => {
    if (message) showErrorToast(message);
  }, [message]);

  // Mark the thread read once on open so the unread badges clear.
  const markedRef = useRef(false);
  useEffect(() => {
    if (!markedRef.current && data) {
      markedRef.current = true;
      markThreadRead(threadId).catch(() => {});
    }
  }, [data, threadId]);

  const title = data?.thread.tournamentName ?? t('messages.thread.loading');

  return (
    <ThreadShell title={title}>
      <MessageList messages={data?.messages ?? []} myRole="player" />
      <Composer
        onSend={async (body) => {
          if (!data) return;
          await sendPlayerMessage(data.thread.tournamentId, body);
          refetch();
        }}
      />
    </ThreadShell>
  );
}

export function MessageThreadPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();

  if (!threadId) {
    const tournamentId = searchParams.get('tournamentId');
    if (!tournamentId) {
      return <ThreadShell title="">{null}</ThreadShell>;
    }
    return <ComposeView tournamentId={tournamentId} />;
  }
  return <ThreadView threadId={threadId} />;
}
